/**
 * Shared state for the Update Schedule review (the Overdue + In-progress
 * sections). Owns the two data fetches, the staged overdue actuals, the staged
 * re-PLANS (whole-span moves — start pushes / pins / duration changes), the
 * in-progress remaining-duration edits, and the statusing commit. The host
 * (UpdateScheduleModal) adds its own chrome + action buttons.
 */
import React from "react";
import {
  f9AutoActuals,
  f9StatusReview,
  workbenchApply,
} from "../../../api/clientV2";
import { onDataDateChanged } from "../../../api/scheduleMutations";
import { apiErrorMessage } from "../../../utils/apiError";
import type {
  AutoActualsResponse,
  StatusActivity,
  StatusReviewResponse,
  WorkbenchActuals,
  WorkbenchDatePush,
} from "../../../models";
import type { Staged } from "./staged";
import type { StagedPlan } from "./plan";
import { basePlanOf } from "./plan";
import { buildActualsList } from "./buildActualsList";
import { buildPlanChanges } from "./buildPlanChanges";

export interface OverdueReviewState {
  review: StatusReviewResponse | null;
  auto: AutoActualsResponse | null;
  loaded: boolean;
  error: string | null;
  setError: (e: string | null) => void;
  /** Locally staged actual dates (what HAPPENED), keyed by activity id. */
  staged: Record<number, Staged>;
  stageActual: (id: number, side: "start" | "finish", date: string) => void;
  /** Locally staged re-plans (the whole effective span), keyed by id. */
  stagedPlans: Record<number, StagedPlan>;
  stagePlan: (id: number, plan: StagedPlan) => void;
  unstagePlan: (id: number) => void;
  /** Clear a staged actual for the side. */
  unstageSlot: (id: number, side: "start" | "finish") => void;
  durationEdits: Record<number, number>;
  editDuration: (id: number, days: number) => void;
  committing: boolean;
  setCommitting: (b: boolean) => void;
  overdueCount: number;
  autoCount: number;
  /** Rows carrying any staged change (actual or re-plan). */
  stagedCount: number;
  /** Only duration edits that differ from the auto-computed default. */
  editsMap: Map<number, number>;
  /** Staged overdue actuals collapsed into one batched apply payload. */
  actualsList: WorkbenchActuals[];
  /** Staged re-plans collapsed into the date_pushes payload. */
  datePushes: WorkbenchDatePush[];
  /** Commit the full statusing (actuals + re-plans + duration edits +
   *  restatement) as ONE batch. Throws. */
  applyStatusing: () => Promise<void>;
}

export function useOverdueReview(scheduleId: number | null): OverdueReviewState {
  const [review, setReview] = React.useState<StatusReviewResponse | null>(null);
  const [auto, setAuto] = React.useState<AutoActualsResponse | null>(null);
  const [staged, setStaged] = React.useState<Record<number, Staged>>({});
  const [stagedPlans, setStagedPlans] = React.useState<Record<number, StagedPlan>>({});
  const [durationEdits, setDurationEdits] = React.useState<Record<number, number>>({});
  const [committing, setCommitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Monotonic sequence so an overlapping reload can't apply stale responses
  // over a newer one (the slow f9StatusReview pair can land out of order).
  const loadSeq = React.useRef(0);
  const reload = React.useCallback(async () => {
    if (scheduleId == null) return;
    const seq = ++loadSeq.current;
    try {
      const [r, a] = await Promise.all([
        f9StatusReview(scheduleId),
        f9AutoActuals(scheduleId),
      ]);
      if (seq !== loadSeq.current) return;
      setReview(r);
      setAuto(a);
      setStaged({});
      setStagedPlans({});
      setDurationEdits({});
      setError(null);
    } catch (e) {
      if (seq === loadSeq.current) {
        setError(apiErrorMessage(e, "Failed to load overdue review"));
      }
    }
  }, [scheduleId]);

  React.useEffect(() => { void reload(); }, [reload]);

  // What counts as "overdue" is defined by the data date — refetch when it
  // changes in the header picker.
  React.useEffect(() => onDataDateChanged(() => { void reload(); }), [reload]);

  const byId = React.useMemo(() => {
    const m = new Map<number, StatusActivity>();
    (review?.activities ?? []).forEach((a) => m.set(a.activity_id, a));
    return m;
  }, [review]);

  const dropPlan = React.useCallback((id: number) => {
    setStagedPlans((p) => {
      if (p[id] === undefined) return p;
      const next = { ...p };
      delete next[id];
      return next;
    });
  }, []);

  const dropStagedSide = React.useCallback(
    (id: number, side: "start" | "finish") => {
      setStaged((p) => {
        const cur = p[id];
        if (!cur || cur[side] === undefined) return p;
        const entry = { ...cur };
        delete entry[side];
        const next = { ...p };
        if (entry.start === undefined && entry.finish === undefined) delete next[id];
        else next[id] = entry;
        return next;
      });
    },
    [],
  );

  // An actual and a re-plan are different answers to the same question —
  // recording what happened drops the row's staged re-plan (its editability
  // changes anyway: an actual start locks the plan's start; an actual finish
  // ends the row). Re-staging the plan afterwards works from the new mode.
  const stageActual = React.useCallback(
    (id: number, side: "start" | "finish", date: string) => {
      setStaged((p) => ({ ...p, [id]: { ...p[id], [side]: date } }));
      dropPlan(id);
    },
    [dropPlan],
  );

  // Staging a re-plan clears a staged actual finish (a re-planned row didn't
  // finish). A staged actual start survives — a started-late row can still
  // push its finish out. A plan slid back onto the committed one un-stages.
  const stagePlan = React.useCallback(
    (id: number, plan: StagedPlan) => {
      const act = byId.get(id);
      if (act) {
        const base = basePlanOf(act);
        if (plan.start === base.start && plan.finish === base.finish) {
          dropPlan(id);
          return;
        }
      }
      setStagedPlans((p) => ({ ...p, [id]: plan }));
      dropStagedSide(id, "finish");
    },
    [byId, dropPlan, dropStagedSide],
  );

  const unstagePlan = React.useCallback((id: number) => dropPlan(id), [dropPlan]);

  const unstageSlot = dropStagedSide;
  const editDuration = React.useCallback(
    (id: number, days: number) => setDurationEdits((p) => ({ ...p, [id]: days })),
    [],
  );

  const overdueCount = React.useMemo(
    () => (review?.activities ?? []).filter((a) => a.start_overdue || a.finish_overdue).length,
    [review],
  );
  const autoCount = auto?.activities.length ?? 0;
  const stagedCount = React.useMemo(
    () => new Set([...Object.keys(staged), ...Object.keys(stagedPlans)]).size,
    [staged, stagedPlans],
  );

  const planChanges = React.useMemo(
    () => buildPlanChanges(stagedPlans, byId, staged, review?.use_expect_end ?? true),
    [stagedPlans, byId, staged, review],
  );

  const editsMap = React.useMemo(() => {
    const m = new Map<number, number>();
    if (!auto) return m;
    const def = new Map(auto.activities.map((a) => [a.activity_id, a.new_duration]));
    for (const [idStr, v] of Object.entries(durationEdits)) {
      const id = Number(idStr);
      // A staged finish completes the activity on commit — its remaining
      // duration becomes 0 and edit_duration would reject it. A re-planned
      // finish (the pin) sets the same thing a duration edit does (the
      // backend rejects the pair). Drop the edit either way.
      if (staged[id]?.finish || stagedPlans[id] !== undefined) continue;
      // Backend requires a whole number of workdays >= 1.
      const days = Math.max(1, Math.round(v));
      if (def.get(id) !== days) m.set(id, days);
    }
    // Stretched block plans imply an explicit duration; the plan wins.
    planChanges.durationEdits.forEach((days, id) => m.set(id, days));
    return m;
  }, [durationEdits, auto, staged, stagedPlans, planChanges]);

  const actualsList = React.useMemo(
    () => buildActualsList(staged, byId),
    [staged, byId],
  );
  const datePushes = planChanges.pushes;

  // The full statusing commit: staged actuals + re-planned dates + duration
  // edits + the in-progress restatement, in one batch (one undo).
  const applyStatusing = React.useCallback(async () => {
    if (scheduleId == null) return;
    const res = await workbenchApply(scheduleId, {
      logic_edits: [],
      actuals_list: actualsList,
      date_pushes: datePushes,
      duration_edits: Array.from(editsMap, ([activity_id, duration_days]) => ({
        activity_id,
        duration_days,
      })),
      apply_actuals: true,
    });
    if (!res.ok) throw new Error(res.error || "Failed to apply updates");
  }, [scheduleId, actualsList, datePushes, editsMap]);

  return {
    review,
    auto,
    loaded: review !== null && auto !== null,
    error,
    setError,
    staged,
    stageActual,
    stagedPlans,
    stagePlan,
    unstagePlan,
    unstageSlot,
    durationEdits,
    editDuration,
    committing,
    setCommitting,
    overdueCount,
    autoCount,
    stagedCount,
    editsMap,
    actualsList,
    datePushes,
    applyStatusing,
  };
}
